import * as React from 'react';
import ReactModal, { OnAfterOpenCallbackOptions } from 'react-modal';
import './modal.scss';
import { useState } from 'react';
import * as IoC from 'typescript-ioc';
import { IModalService } from '../../../infrastructure/modal';
import { IModalContextData, ModalContext, ModalContextData } from './modal-context';

type IModalProps = Omit<ReactModal.Props, 'shouldCloseOnOverlayClick'> & {
    /**
     * when true - opening this modal does NOT hide whichever modal is currently on top of the stack.
     * use for a modal which is meant to visually stack on top of another modal instead of replacing it.
     */
    keepPreviousModalVisible?: boolean;
};

ReactModal.setAppElement('body');

/**
 * Generic modal.
 * Supposed to be used to display custom content in popup windows.
 * Implemented as a wrapper over the `ReactModal` with minor changes described below:
 * 1) apply custom styling with .oe-modal class (only affects overlay and modal container, NOT affecting modal layout/content).
 * 2) 'shouldCloseOnOverlayClick' is NOT exposed directly to application.
 *      Instead, presence of 'Close' button inside the modal ensure that 'shouldCloseOnOverlayClick' is set to true
 *      This is done to avoid duplication of close handler in the 'onRequestClose' callback of ReactModal
 *      and in the onClick of close buttons (which might be multiply inside modal, such as 'x' and 'Close')
 *
 * Use this modal if you want to display something very custom inside the modal window
 * Don't use this modal if you need common-styled modal window with header or footer(or both) and scrollable body. Use CommonModal instead.
 * @param props
 * @constructor
 */
const Modal = (props : IModalProps) => {
    const { keepPreviousModalVisible, ...reactModalProps } = props;
    const modalService = IoC.Container.get<IModalService>(IModalService);
    const className = 'oe-modal' + (props.className ? ' ' + props.className : '');

    const [modalContext] = useState<IModalContextData>(new ModalContextData());
    const [modalKey] = useState(modalService.generateModalKey());

    // assign onRequestClose to context so it can be reused by all 'close' buttons rendered inside the modal
    modalContext.closeButtonClickHandler = props.onRequestClose;

    const finalProps : ReactModal.Props = {
        ...reactModalProps,
        className: className,
        // always handle shouldCloseOnOverlayClick but trigger onRequestClose only if at least one cancel button is mounted
        shouldCloseOnOverlayClick: true,
        onRequestClose: (event: React.MouseEvent | React.KeyboardEvent) => {
            if (modalContext.isCloseButtonMounted()) {
                props.onRequestClose(event);
            }
        },
        onAfterOpen: (e: OnAfterOpenCallbackOptions) => {
            modalService.addModal(modalKey, e.overlayEl, !keepPreviousModalVisible);
        },
        onAfterClose: () => {
            modalService.removeModal(modalKey);
        }
    };

    return (
        <ModalContext.Provider value={modalContext}>
            <ReactModal { ...finalProps } />
        </ModalContext.Provider>
    );
};

export { Modal, IModalProps };